import React, { useMemo } from 'react'
import { Form, DatePicker as DatePickerAntd } from 'antd'
import { FormItemProps } from 'antd/lib/form'
import { RangePickerProps } from 'antd/lib/date-picker/interface'
import dp from 'dot-prop-immutable'
import en_US from 'antd/lib/date-picker/locale/en_US'
import es_ES from 'antd/lib/date-picker/locale/es_ES'
import moment from 'moment'
import pt_BR from 'antd/lib/date-picker/locale/pt_BR'

import { useT } from 'src/core/hooks/use-translation'
import i18n from 'src/config/i18n'

type Props = Omit<RangePickerProps, 'name' | 'placeholder'> &
  FormItemProps & {
    formikProps: any
    name: [string, string]
    placeholder: [string, string]
  }

export default function DatePickerRange({
  formikProps,
  label,
  name,
  onChange,
  placeholder,
  required = true,
  showTime = { format: 'HH:mm' },
  size = 'large',
  ...props
}: Props) {
  const { t } = useT()

  const error = useMemo(
    () =>
      dp.get(formikProps.errors, name[0]) ||
      dp.get(formikProps.errors, name[1]),
    [formikProps.errors, name]
  )
  const touched = useMemo(
    () => dp.get(formikProps.touched, name[0]),
    [formikProps.touched, name]
  )
  const value1 = useMemo(
    () => dp.get(formikProps.values, name[0]),
    [formikProps.values, name]
  )
  const value2 = useMemo(
    () => dp.get(formikProps.values, name[1]),
    [formikProps.values, name]
  )

  const isFormat = typeof showTime === 'object' ? ` ${showTime.format}` : ''
  const locale = ['pt', 'pt_br', 'pt-br'].includes(
    i18n.language.toLocaleLowerCase()
  )
    ? pt_BR
    : ['es', 'es_es', 'es-es'].includes(i18n.language.toLocaleLowerCase())
    ? es_ES
    : en_US

  return (
    <Form.Item
      colon={false}
      data-test-id="select-period"
      help={touched && typeof error === 'string' ? t(error) : ''}
      label={t(label)}
      required={required}
      validateStatus={
        error && touched ? 'error' : !error && touched ? 'success' : ''
      }
      hasFeedback
    >
      <DatePickerAntd.RangePicker
        {...props}
        allowClear={false}
        format={`DD/MM/YYYY${isFormat}`}
        locale={locale}
        onChange={value => {
          formikProps.setFieldValue(
            [name[0]],
            value ? value[0].seconds(0).millisecond(0).unix() : undefined
          )
          formikProps.setFieldValue(
            name[1],
            value ? value[1].seconds(0).millisecond(0).unix() : undefined
          )
          if (onChange) {
            onChange(value, null)
          }
        }}
        onOpenChange={() =>
          formikProps.setTouched({
            ...formikProps.touched,
            [name[0]]: true
          })
        }
        placeholder={[t(placeholder[0]), t(placeholder[1])]}
        size={size}
        showTime={showTime}
        value={
          value1
            ? [moment.unix(value1).utc(), moment.unix(value2).utc()]
            : undefined
        }
      />
    </Form.Item>
  )
}
